// The segment registry and the rules governing which segments a given host may see.
//
// Dependency-free on purpose: the resolution/gating rules are the part with real branching, so
// they live here as pure functions and are unit-tested in registry.test.js (node --test), per
// the repo's frontend testing convention. The React pages consume these results.

import businessContent from './business.js'
import landlordsContent from './landlords.js'
import partnersContent from './partners.js'
import schoolsContent from './schools.js'

// Which platform brands (brand_key, set by the domain: theme/brands.js) offer each audience
// segment. joulelingo.com is the schools product and kilosight.com the one for businesses,
// hospitality and landlords, so neither pitches the other's audience. 'power', the pre-launch
// default host, offers everything. A segment without `brands` shows on every brand.
const SCHOOLS_BRANDS = ['power', 'joulelingo']
const BUSINESS_BRANDS = ['power', 'kilosight']
const SEGMENT_BRANDS = new Set([...SCHOOLS_BRANDS, ...BUSINESS_BRANDS])

// Order drives the homepage chooser. Education first: it is the strongest shipped story.
export const SEGMENTS = [
    {
        key: 'schools',
        path: '/for/schools',
        // Chooser card copy. Each blurb states one segment's distinct reason to buy — they must
        // not be interchangeable, or the chooser gives a visitor no basis for choosing.
        chooserTitle: 'Schools & education',
        chooserBlurb:
            'Turn energy into something pupils act on, and give trusts one view across every school.',
        content: schoolsContent,
        brands: SCHOOLS_BRANDS
    },
    {
        key: 'landlords',
        path: '/for/landlords',
        chooserTitle: 'Landlords & estates',
        chooserBlurb:
            'Compare properties side by side, find the outliers, and evidence improvements to owners.',
        content: landlordsContent,
        brands: BUSINESS_BRANDS
    },
    {
        key: 'business',
        path: '/for/business',
        chooserTitle: 'Multi-site business',
        chooserBlurb:
            'See what your sites burn after closing time. It is usually the cheapest saving available.',
        content: businessContent,
        brands: BUSINESS_BRANDS
    },
    {
        key: 'partners',
        path: '/for/partners',
        chooserTitle: 'Partners & resellers',
        chooserBlurb:
            'Run the platform under your own brand, with your licence, your clients and your pricing.',
        content: partnersContent,
        // Platform-operator-only: recruiting resellers on a partner's own white-label domain
        // would pitch that partner's prospects on becoming their competitors.
        platformHostOnly: true
    }
]

/** The segment with this key, or undefined. */
export function getSegment(key) {
    return SEGMENTS.find((segment) => segment.key === key)
}

/**
 * Whether this segment belongs to the brand. An unknown or missing key (branding still in
 * flight, an old payload without brand_key) reads as the default brand, which offers all.
 */
function offeredByBrand(segment, brandKey) {
    if (!segment.brands) return true
    const known = SEGMENT_BRANDS.has(brandKey) ? brandKey : 'power'
    return segment.brands.includes(known)
}

/**
 * Segments to offer on this host.
 *
 * `isPartnerHost` true (a white-label subdomain) drops the platform-operator-only entries but
 * keeps the audience segments — a partner reselling to schools benefits from a schools page
 * carrying their brand. `brandKey` (brand.brand_key) keeps each platform brand to its own
 * audiences; a partner host carries the brand of the domain it sits under.
 */
export function getVisibleSegments(isPartnerHost, brandKey) {
    return SEGMENTS.filter(
        (segment) =>
            !(segment.platformHostOnly && isPartnerHost) &&
            offeredByBrand(segment, brandKey)
    )
}

/**
 * What a `/for/<key>` request should do.
 *
 * Returns `{ action: 'render', segment }` or `{ action: 'redirect', to: '/' }`. Every failure
 * mode lands on the homepage rather than a 404: these URLs go in emails, ads and sales decks,
 * and a stale one should still show the visitor something useful.
 *
 * @param {object} options
 * @param {string} options.key           the `:segmentKey` route param
 * @param {boolean} options.isPartnerHost host resolved to a partner (brand.is_partner)
 * @param {boolean} options.isAuthenticated a signed-in user followed a marketing link
 * @param {boolean} options.isBeta       public routes disabled by beta mode
 * @param {string} [options.brandKey]    brand.brand_key; another brand's audience goes home
 */
export function resolveSegmentRoute({
    key,
    isPartnerHost,
    isAuthenticated,
    isBeta,
    brandKey
}) {
    const home = { action: 'redirect', to: '/' }

    // A signed-in user gets the app, not marketing chrome. Checked before anything else so the
    // outcome does not depend on which segment they happened to click.
    if (isAuthenticated) return home
    // Beta builds serve no public site; degrade to the homepage rather than 404.
    if (isBeta) return home

    const segment = getSegment(key)
    if (!segment) return home
    if (segment.platformHostOnly && isPartnerHost) return home
    if (!offeredByBrand(segment, brandKey)) return home

    return { action: 'render', segment }
}

/** Whether platform pricing may be shown on this host (page body and nav links alike). */
export function showsPlatformPricing(isPartnerHost) {
    return !isPartnerHost
}

/**
 * A segment's features as page sections.
 *
 * A segment that declares `audiences` gets one section per audience, in that order, each with
 * the features naming its key. One without gets a single untitled section of every feature.
 */
export function featureSections(content) {
    if (!content.audiences?.length) {
        return [
            { key: 'all', title: null, lede: null, features: content.features }
        ]
    }
    return content.audiences.map((audience) => ({
        ...audience,
        features: content.features.filter(
            (feature) => feature.audience === audience.key
        )
    }))
}

/**
 * The segment's customer reference line for this host, or null.
 *
 * A `platformHostOnly` reference is a customer who agreed to be named by the platform, not by a
 * partner reselling under its own brand, so a partner host never shows it.
 */
export function referenceLine(content, { isPartnerHost, productName }) {
    const reference = content.reference
    if (!reference) return null
    if (reference.platformHostOnly && isPartnerHost) return null
    return reference.quote.replace('{product}', productName)
}
